import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/care")({
  head: () => ({
    meta: [
      { title: "Care Guide — European Wig Luxury" },
      { name: "description", content: "How to wash, style, and store virgin European hair wigs so they last for years." },
      { property: "og:title", content: "Care Guide — European Wig Luxury" },
      { property: "og:description", content: "Washing, styling, and storage guidance for virgin European hair." },
      { property: "og:url", content: "https://europeanwigluxury.com/care" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/care" }],
  }),
  component: Care,
});

const sections = [
  {
    title: "Washing",
    body: [
      "Wash every 8–15 wears, or when the hair starts to feel weighed down.",
      "Detangle from the ends up with a wide-tooth comb before wetting the hair.",
      "Use a sulfate-free shampoo made for human-hair wigs. Work the lather gently through the length — do not scrub the cap or knots.",
      "Rinse in cool water (warm water loosens knots over time).",
      "Follow with a lightweight conditioner, applied from mid-shaft to ends only. Avoid the cap and knot base.",
    ],
  },
  {
    title: "Drying & styling",
    body: [
      "Blot excess water with a microfiber towel — never wring or twist the hair.",
      "Air-dry on a soft wig stand or a mannequin head. Avoid styrofoam, which can stretch the cap.",
      "Virgin hair takes heat well, but keep tools at or below 300°F / 150°C and always use a heat protectant. Excessive heat is the fastest way to shorten your wig's lifespan.",
      "For a fresh restyle, mist lightly with water and use rollers, or bring your piece in for a professional wash-and-set.",
    ],
  },
  {
    title: "Storage",
    body: [
      "Store on a wig stand or hang inside-out on a hanger to keep the cap in shape.",
      "Keep out of direct sunlight — UV will lift color over time, even on unprocessed hair.",
      "Between wears, keep a silk or satin scarf between the hair and rougher surfaces to reduce friction.",
    ],
  },
  {
    title: "What to avoid",
    body: [
      "Dye, bleach, and permanent chemical treatments — they undo the virgin quality of the hair.",
      "Sleeping in the wig if it can be helped; use a satin pillowcase if you do.",
      "Long chlorine or salt-water exposure. Rinse promptly if it happens.",
    ],
  },
];

function Care() {
  return (
    <>
      <section className="border-b border-border/70 bg-secondary/40">
        <div className="container-luxe py-16 md:py-20">
          <p className="eyebrow">Preserve the craft</p>
          <h1 className="mt-3 font-display text-4xl md:text-6xl">Care Guide</h1>
          <p className="mt-4 max-w-xl text-muted-foreground">Virgin European hair rewards patience. Treated well, your wig will look this good for years.</p>
        </div>
      </section>
      <section className="container-luxe py-16 md:py-20">
        <div className="mx-auto max-w-3xl space-y-14">
          {sections.map((s) => (
            <div key={s.title}>
              <h2 className="font-display text-2xl md:text-3xl">{s.title}</h2>
              <ul className="mt-4 space-y-3">
                {s.body.map((b, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-bronze" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
